import React, { useState, useEffect } from 'react';
import { aiApi, clientsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Mail, FileText, Calendar, MessageSquare, Sparkles, 
  Send, Copy, Trash2, Edit, Loader2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const DRAGON_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/ot58j39v_Transparent%20BG%20Dragon.png';

const Drafts = () => {
  const [drafts, setDrafts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [activeTab, setActiveTab] = useState('compose');
  
  const [draftForm, setDraftForm] = useState({
    draft_type: 'email',
    recipient_name: '',
    recipient_email: '',
    subject: '',
    context: '',
    tone: 'professional',
    client_id: 'none',
    additional_instructions: ''
  });

  const [generatedDraft, setGeneratedDraft] = useState(null);

  const fetchData = async () => {
    try {
      const [draftsRes, clientsRes] = await Promise.all([
        aiApi.getDrafts({}),
        clientsApi.getAll()
      ]);
      setDrafts(draftsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleGenerateDraft = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setGeneratedDraft(null);

    try {
      // Convert "none" client_id back to empty string for API
      const apiData = {
        ...draftForm,
        client_id: draftForm.client_id === 'none' ? '' : draftForm.client_id
      };
      const result = await aiApi.createDraft(apiData);
      setGeneratedDraft(result.data);
      toast.success('Draft generated successfully!');
      fetchData(); // Refresh drafts list
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate draft';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSendDraft = async (draftId) => {
    try {
      await aiApi.sendDraft(draftId);
      toast.success('Email sent successfully!');
      fetchData();
      setSelectedDraft(null);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send email';
      toast.error(message);
    }
  };

  const handleDeleteDraft = async (draftId) => {
    try {
      await aiApi.deleteDraft(draftId);
      toast.success('Draft deleted');
      fetchData();
      setSelectedDraft(null);
    } catch (error) {
      toast.error('Failed to delete draft');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getDraftTypeIcon = (type) => {
    switch (type) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'memo': return <FileText className="w-4 h-4" />;
      case 'meeting_notes': return <Calendar className="w-4 h-4" />;
      case 'follow_up': return <MessageSquare className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-700';
      case 'draft': return 'bg-gray-100 text-gray-700';
      case 'pending_review': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="drafts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">AI Drafts</h1>
          <p className="text-navy/60 mt-1">Generate emails, memos, and meeting notes with AI</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white border border-navy/10">
          <TabsTrigger value="compose" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="drafts" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            My Drafts ({drafts.length})
          </TabsTrigger>
        </TabsList>

        {/* Compose Tab */}
        <TabsContent value="compose">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Form */}
            <Card className="bg-white border-navy/10">
              <CardHeader>
                <CardTitle className="font-['Amaranth'] flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#E171EA]" />
                  Generate Draft
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleGenerateDraft} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Draft Type</Label>
                    <Select 
                      value={draftForm.draft_type} 
                      onValueChange={(value) => setDraftForm({ ...draftForm, draft_type: value })}
                    >
                      <SelectTrigger data-testid="draft-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" /> Email
                          </div>
                        </SelectItem>
                        <SelectItem value="memo">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" /> Internal Memo
                          </div>
                        </SelectItem>
                        <SelectItem value="meeting_notes">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" /> Meeting Notes
                          </div>
                        </SelectItem>
                        <SelectItem value="follow_up">
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" /> Follow-up
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {(draftForm.draft_type === 'email' || draftForm.draft_type === 'follow_up') && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Recipient Name</Label>
                        <Input
                          value={draftForm.recipient_name}
                          onChange={(e) => setDraftForm({ ...draftForm, recipient_name: e.target.value })}
                          placeholder="John Smith"
                          data-testid="recipient-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Recipient Email</Label>
                        <Input
                          type="email"
                          value={draftForm.recipient_email}
                          onChange={(e) => setDraftForm({ ...draftForm, recipient_email: e.target.value })}
                          placeholder="john@example.com"
                          data-testid="recipient-email-input"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Subject / Topic</Label>
                    <Input
                      value={draftForm.subject}
                      onChange={(e) => setDraftForm({ ...draftForm, subject: e.target.value })}
                      placeholder="What is this about?"
                      data-testid="subject-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tone</Label>
                      <Select 
                        value={draftForm.tone} 
                        onValueChange={(value) => setDraftForm({ ...draftForm, tone: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="friendly">Friendly</SelectItem>
                          <SelectItem value="formal">Formal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Related Client</Label>
                      <Select 
                        value={draftForm.client_id} 
                        onValueChange={(value) => setDraftForm({ ...draftForm, client_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Optional" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {clients.map(client => (
                            <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Context / Key Points *</Label>
                    <Textarea
                      value={draftForm.context}
                      onChange={(e) => setDraftForm({ ...draftForm, context: e.target.value })}
                      placeholder="What should this draft include? Key points, main message, any specific details..."
                      rows={4}
                      required
                      data-testid="context-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Additional Instructions</Label>
                    <Input
                      value={draftForm.additional_instructions}
                      onChange={(e) => setDraftForm({ ...draftForm, additional_instructions: e.target.value })}
                      placeholder="Any special instructions for the AI..."
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90"
                    disabled={generating}
                    data-testid="generate-draft-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Draft
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Generated Draft Preview */}
            <Card className="bg-white border-navy/10">
              <CardHeader>
                <CardTitle className="font-['Amaranth']">Preview</CardTitle>
              </CardHeader>
              <CardContent>
                {generatedDraft ? (
                  <div className="space-y-4">
                    {generatedDraft.subject && (
                      <div>
                        <Label className="text-navy/60">Subject</Label>
                        <p className="font-medium text-[#1A2B5E] mt-1">{generatedDraft.subject}</p>
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-navy/60">Content</Label>
                      <div className="mt-2 p-4 bg-navy/5 rounded-lg whitespace-pre-wrap text-sm">
                        {generatedDraft.content}
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(generatedDraft.content)}
                        className="flex-1"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                      
                      {generatedDraft.draft_type === 'email' && draftForm.recipient_email && (
                        <Button
                          className="flex-1 bg-[#37C563] text-white hover:bg-[#37C563]/90"
                          onClick={() => handleSendDraft(generatedDraft.draft_id)}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Send Email
                        </Button>
                      )}
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          setGeneratedDraft(null);
                          handleGenerateDraft({ preventDefault: () => {} });
                        }}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="empty-state py-12">
                    <img src={DRAGON_MASCOT} alt="Dragon" className="w-24 h-24 mb-4" />
                    <p className="text-navy/60">
                      {generating ? 'AI is crafting your draft...' : 'Your generated draft will appear here'}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Drafts Tab */}
        <TabsContent value="drafts">
          {drafts.length === 0 ? (
            <Card className="bg-white border-navy/10">
              <CardContent className="py-16">
                <div className="empty-state">
                  <img src={DRAGON_MASCOT} alt="Dragon" className="w-32 h-32 mb-4" />
                  <h3 className="text-xl font-['Amaranth'] text-[#1A2B5E] mb-2">No drafts yet</h3>
                  <p className="text-navy/60 mb-4">Generate your first AI draft!</p>
                  <Button
                    className="bg-[#40CFDE] text-[#1A2B5E]"
                    onClick={() => setActiveTab('compose')}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create Draft
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {drafts.map((draft) => (
                <Card 
                  key={draft.id} 
                  className="bg-white border-navy/10 card-hover cursor-pointer"
                  onClick={() => setSelectedDraft(draft)}
                  data-testid={`draft-card-${draft.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {getDraftTypeIcon(draft.type)}
                        <span className="text-sm font-medium text-[#1A2B5E] capitalize">
                          {draft.type?.replace('_', ' ')}
                        </span>
                      </div>
                      <Badge className={getStatusColor(draft.status)}>
                        {draft.status}
                      </Badge>
                    </div>
                    
                    {draft.subject && (
                      <h4 className="font-medium text-[#1A2B5E] mb-2 line-clamp-1">
                        {draft.subject}
                      </h4>
                    )}
                    
                    <p className="text-sm text-navy/60 line-clamp-3">
                      {draft.content}
                    </p>
                    
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-navy/5 text-xs text-navy/40">
                      {draft.client_id && (
                        <span>{getClientName(draft.client_id)}</span>
                      )}
                      <span>{new Date(draft.created_at).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Draft Detail Dialog */}
      <Dialog open={!!selectedDraft} onOpenChange={() => setSelectedDraft(null)}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          {selectedDraft && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  {getDraftTypeIcon(selectedDraft.type)}
                  <span className="text-sm font-medium capitalize">
                    {selectedDraft.type?.replace('_', ' ')}
                  </span>
                  <Badge className={getStatusColor(selectedDraft.status)}>
                    {selectedDraft.status}
                  </Badge>
                </div>
                {selectedDraft.subject && (
                  <DialogTitle className="font-['Amaranth']">{selectedDraft.subject}</DialogTitle>
                )}
              </DialogHeader>
              
              <div className="mt-4 space-y-4">
                {selectedDraft.recipient_email && (
                  <div>
                    <Label className="text-navy/60">To</Label>
                    <p className="text-[#1A2B5E]">
                      {selectedDraft.recipient_name && `${selectedDraft.recipient_name} `}
                      &lt;{selectedDraft.recipient_email}&gt;
                    </p>
                  </div>
                )}
                
                <div>
                  <Label className="text-navy/60">Content</Label>
                  <div className="mt-2 p-4 bg-navy/5 rounded-lg whitespace-pre-wrap text-sm">
                    {selectedDraft.content}
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(selectedDraft.content)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                  
                  {selectedDraft.type === 'email' && 
                   selectedDraft.recipient_email && 
                   selectedDraft.status !== 'sent' && (
                    <Button
                      className="bg-[#37C563] text-white hover:bg-[#37C563]/90"
                      onClick={() => handleSendDraft(selectedDraft.id)}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Email
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDeleteDraft(selectedDraft.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Drafts;
